import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

type Audience = 'client' | 'contractor';

const steps: Record<Audience, { title: string; body: string }[]> = {
  client: [
    {
      title: 'Post Your Job',
      body: 'Describe what you need: title, location, category, and budget. Your job goes live immediately for contractors to find.',
    },
    {
      title: 'Review Contractors',
      body: 'Contractors message you directly. Review their profiles, verified credentials, ratings, and past work before deciding.',
    },
    {
      title: 'Select and Track',
      body: "Choose the contractor you trust, confirm the work is done, and leave a review. Your feedback helps the whole community.",
    },
  ],
  contractor: [
    {
      title: 'Browse Open Jobs',
      body: 'Find jobs in your province by category. No subscription or bidding fees - just browse and reach out.',
    },
    {
      title: 'Contact the Client',
      body: 'Message the client directly to introduce yourself and discuss the work. First message starts the thread.',
    },
    {
      title: 'Deliver and Build Your Reputation',
      body: "Complete the job, upload before-and-after photos, and collect your review. Every job strengthens your profile.",
    },
  ],
};

const HowItWorksSection: React.FC = () => {
  const [audience, setAudience] = useState<Audience>('client');
  const navigate = useNavigate();
  const activeSteps = steps[audience];

  return (
    <section className="bg-primary-container rounded-2xl p-6 text-on-primary md:py-24 md:px-container-margin md:max-w-7xl md:mx-auto md:bg-transparent md:text-on-background">
      <h2 className="font-headline-sm text-headline-sm-mobile text-on-primary-container mb-4 md:font-headline-md md:text-headline-md md:text-primary md:mb-8 md:text-center">
        How Buildon Works
      </h2>

      {/* Audience tabs */}
      <div className="flex justify-center mb-8 md:mb-12">
        <div className="flex bg-surface-container rounded-xl p-1 gap-1">
          {(['client', 'contractor'] as Audience[]).map(a => (
            <button
              key={a}
              onClick={() => setAudience(a)}
              className={`px-6 py-2 rounded-lg font-label-bold capitalize transition-colors ${
                audience === a
                  ? 'bg-primary text-on-primary shadow-sm'
                  : 'text-on-surface-variant hover:text-on-surface'
              }`}
            >
              {a === 'client' ? 'For Clients' : 'For Contractors'}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-6 md:grid md:grid-cols-1 md:gap-16 md:relative lg:grid-cols-3">
        {/* Connector line - desktop only */}
        <div className="hidden md:block absolute top-12 left-1/4 right-1/4 h-0.5 bg-outline-variant/20" />

        {activeSteps.map((step, i) => (
          <div key={i} className="flex gap-4 md:flex-col md:items-center md:text-center md:gap-6 md:relative">
            <div className="w-8 h-8 bg-secondary-container text-on-secondary-container rounded-full flex items-center justify-center font-bold flex-shrink-0 md:w-16 md:h-16 md:bg-primary md:text-on-primary md:rounded-full md:flex md:items-center md:justify-center md:font-headline-md md:text-headline-md md:shadow-lg md:z-10">
              {i + 1}
            </div>
            <div>
              <h4 className="font-label-bold text-on-primary-container text-base md:font-headline-sm md:text-headline-sm md:text-primary md:mb-3">
                {step.title}
              </h4>
              <p className="font-body-md text-on-primary-container opacity-90 md:font-body-md md:text-body-md md:text-on-surface-variant">
                {step.body}
              </p>
            </div>
          </div>
        ))}
      </div>

      <button
        onClick={() => navigate('/register')}
        className="w-full mt-8 py-4 bg-secondary-container text-on-secondary-container rounded-xl font-headline-sm hover:opacity-90 transition-opacity md:hidden"
      >
        Get Started Now
      </button>
    </section>
  );
};

export default HowItWorksSection;
